import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { TRADE_TYPE } from 'src/common/constants';
import { changeScrollBehavior } from 'src/common/helpers';
import { Button, Drawer } from 'src/components/core';
import { useAppSelector } from 'src/hooks';
import { RiskLimit } from 'src/types/configV2';

import { TransferModal } from '../../common/modals/transferModal';
import { PreferencesModal } from '../modals/preferencesModal';
import { RiskLimitModal } from '../modals/riskLimitModal';

interface PreferencesDrawerProps {
    isOpen: boolean;
    toggleDrawer: () => void;
}

const StyledDrawer = styled(Drawer)`
    ${({ theme: { colors, typography } }) => `
        .container{
            margin: 8px;
            background: ${colors.background.primary};
            border-radius: 13px;
            .item {
                text-align: center;
                padding: 16px;
                background: transparent;
                box-shadow: none;
                border: none;
                width: 100%;
                color: ${colors.text.primary};
                font-size: ${typography.size.mobile.paragraph.p1};
                font-weight: ${typography.weight.normal};
                :not(:first-child){
                    border-top: 1px solid ${colors.border.dark};
                    border-radius: 0px;
                }
            }
        }
    `}
`;

const PreferencesDrawer: FC<PreferencesDrawerProps> = ({ isOpen, toggleDrawer }) => {
    const { t } = useTranslation();
    const { currentSymbol, symbolSettings } = useAppSelector((state) => state.derivatives);
    const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [isRiskLimitModalOpen, setIsRiskLimitModalOpen] = useState(false);
    const [currentRiskInfo, setCurrentRiskInfo] = useState({} as RiskLimit);
    const [buyRiskInfo, setBuyRiskInfo] = useState({} as RiskLimit);
    const [sellRiskInfo, setSellRiskInfo] = useState({} as RiskLimit);
    const [side, setSide] = useState('');

    useEffect(() => {
        symbolSettings?.riskLimit.map((item) => {
            currentSymbol?.baseTokenFutures?.riskLimits.map((riskLimit) => {
                if (item.side == TRADE_TYPE.BUY_OPEN) {
                    if (item.riskLimitId == riskLimit.riskLimitId) {
                        setBuyRiskInfo(item);
                    }
                }
                if (item.side == TRADE_TYPE.SELL_OPEN) {
                    if (item.riskLimitId == riskLimit.riskLimitId) {
                        setSellRiskInfo(item);
                    }
                }
            });
        });
    }, [currentSymbol, symbolSettings]);
    const closePreferencesModal = () => {
        setIsPreferencesModalOpen(false);
    };
    const openPreferencesModal = () => {
        toggleDrawer();
        setIsPreferencesModalOpen(true);
    };
    const handleTransferModal = () => {
        setIsTransferModalOpen(!isTransferModalOpen);
        if (!isTransferModalOpen) {
            toggleDrawer();
        }
    };
    const handleRiskLimitModal = (side?: string) => {
        if (side) {
            if (side == TRADE_TYPE.BUY_OPEN) {
                setCurrentRiskInfo(buyRiskInfo);
                setSide(TRADE_TYPE.BUY_OPEN);
            } else {
                setCurrentRiskInfo(sellRiskInfo);
                setSide(TRADE_TYPE.SELL_OPEN);
            }
        }
        setIsRiskLimitModalOpen(!isRiskLimitModalOpen);
        if (!isRiskLimitModalOpen) {
            toggleDrawer();
        }
    };
    useEffect(() => {
        changeScrollBehavior(isTransferModalOpen || isRiskLimitModalOpen);
    }, [isTransferModalOpen, isRiskLimitModalOpen]);
    return (
        <>
            {isOpen && (
                <StyledDrawer handleClose={toggleDrawer} anchor="bottom" open={isOpen}>
                    <div className="container">
                        <Button className="item" onClick={handleTransferModal}>
                            {t('transfer')}
                        </Button>
                        <Button
                            className="item"
                            onClick={() => handleRiskLimitModal(TRADE_TYPE.BUY_OPEN)}
                        >
                            {`${t('risk_limit')} (${t('long')})`}
                        </Button>
                        <Button
                            className="item"
                            onClick={() => handleRiskLimitModal(TRADE_TYPE.SELL_OPEN)}
                        >
                            {`${t('risk_limit')} (${t('short')})`}
                        </Button>
                        <Button className="item" onClick={openPreferencesModal}>
                            {t('trading_preferences')}
                        </Button>
                    </div>
                </StyledDrawer>
            )}
            {isPreferencesModalOpen && (
                <PreferencesModal
                    isOpen={isPreferencesModalOpen}
                    handleClose={closePreferencesModal}
                />
            )}
            {isTransferModalOpen && (
                <TransferModal open={isTransferModalOpen} handleClose={handleTransferModal} />
            )}
            {isRiskLimitModalOpen && (
                <RiskLimitModal
                    open={isRiskLimitModalOpen}
                    handleClose={handleRiskLimitModal}
                    currentRiskInfo={currentRiskInfo}
                    side={side}
                />
            )}
        </>
    );
};

export { PreferencesDrawer };
